import React from 'react';
import { Link } from 'react-router-dom';
import { getCountdown, formatDate, getDaysToGo } from '../../utils/attendeeUtils';
import { toGoogleMapsDirections } from '../../utils/helpers';

const NextEventCard = ({ event, registration, variant = 'hero' }) => {
  if (!event) return null;

  const countdown = getCountdown(event.start_date || event.start);
  const daysToGo = getDaysToGo(event.start_date || event.start);
  const venue = event.venue_name || event.venue;
  const city = event.city || event.location;
  const address = event.address || [venue, city].filter(Boolean).join(', ');
  const imageUrl = event.image_url;
  const isPaid = registration?.payment_status === 'paid';
  const isCancelled = registration?.status === 'cancelled';

  if (variant === 'compact') {
    return (
      <Link
        to={`/my-events/${event.id}`}
        className="block bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
      >
        <div className="flex items-center gap-4 p-4">
          {imageUrl && (
            <img src={imageUrl} alt={event.title || event.event} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white truncate">
              {event.title || event.event}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {formatDate(event.start_date || event.start)}
            </p>
            {venue && <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{venue}</p>}
          </div>
          <div className="text-right flex-shrink-0">
            <span className="inline-block px-2 py-1 text-xs font-bold rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300">
              {daysToGo}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <div className="relative rounded-2xl overflow-hidden shadow-xl mb-8">
      {/* Background image */}
      {imageUrl && (
        <div className="absolute inset-0">
          <img src={imageUrl} alt={event.title || event.event} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-black/30" />
        </div>
      )}
      {!imageUrl && (
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-purple-700" />
      )}

      <div className="relative p-6 sm:p-8 text-white">
        <div className="flex items-center gap-2 mb-3">
          <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold uppercase tracking-wider">
            Your Next Event
          </span>
          {countdown && (
            <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-bold">
              {countdown} to go
            </span>
          )}
        </div>

        <h2 className="text-2xl sm:text-3xl font-bold mb-2">
          {event.title || event.event}
        </h2>

        <div className="flex flex-wrap items-center gap-4 text-sm text-white/90 mb-4">
          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {formatDate(event.start_date || event.start)}
          </span>
          {venue && (
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {venue}
            </span>
          )}
          {city && (
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
              </svg>
              {city}
            </span>
          )}
        </div>

        {/* Registration status badges */}
        {registration && (
          <div className="flex items-center gap-2 mb-4">
            {isCancelled ? (
              <span className="px-2.5 py-1 bg-red-500/80 backdrop-blur-sm rounded-full text-xs font-medium">
                Cancelled
              </span>
            ) : (
              <>
                <span className="px-2.5 py-1 bg-green-500/80 backdrop-blur-sm rounded-full text-xs font-medium">
                  Registered
                </span>
                {isPaid ? (
                  <span className="px-2.5 py-1 bg-blue-500/80 backdrop-blur-sm rounded-full text-xs font-medium">
                    Paid
                  </span>
                ) : (
                  <span className="px-2.5 py-1 bg-yellow-500/80 backdrop-blur-sm rounded-full text-xs font-medium">
                    Payment Pending
                  </span>
                )}
              </>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          <Link
            to={`/my-events/${event.id}`}
            className="inline-flex items-center px-4 py-2.5 bg-white text-gray-900 rounded-lg font-medium text-sm hover:bg-gray-100 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            View Event
          </Link>
          {registration && (
            <Link
              to="/attendee/tickets"
              className="inline-flex items-center px-4 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-lg font-medium text-sm hover:bg-white/30 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
              View Ticket
            </Link>
          )}
          {address && (
            <a
              href={toGoogleMapsDirections(address)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-lg font-medium text-sm hover:bg-white/30 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              Directions
            </a>
          )}
          <Link
            to="/schedule"
            className="inline-flex items-center px-4 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-lg font-medium text-sm hover:bg-white/30 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Schedule
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NextEventCard;
